import { reportDashboardMetricValues } from '../dashboard/reportMetricValues';
import { useQuery } from '@tanstack/react-query';
import type { DashboardDateRange } from '@/components/dashboard/model';
import type { ShigoApiConfig } from '../../lib/shigo-api-client';
import { readShigoAppConfig } from '../../lib/config';
import { useShigoSession } from '../../lib/useShigoSession';
import { dashboardRangeKey } from '../dashboard/hooks';
import {
  supersedableDashboardQueryFn,
  useAbortSupersededDashboardQueries,
} from '../dashboard/supersededDashboardRequests';
import { createReportExplorerClient } from '../reporting/reportExplorer/api';
import type { ReportRequest } from '../reporting/reportExplorer/model';
import {
  ReportDimension,
  ReportExplorerScope,
  ReportMetric,
  ReportType,
} from '../reporting/reportExplorer/reportEnums';
import { buildTimeShiftReportRequest } from '../reporting/reportExplorer/reportRequest';

export function createAgencyDashboardClient(options: {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}) {
  const api = createReportExplorerClient(options);
  return {
    async report(agencyId: string, range: DashboardDateRange, signal?: AbortSignal) {
      const request: ReportRequest = {
        source: ReportType.Exchange,
        dimensions: [ReportDimension.Day],
        metrics: [
          ReportMetric.Day,
          ReportMetric.AdImpressionsLi,
          ReportMetric.RevenueLi,
          ReportMetric.RpmLi,
          ReportMetric.AdClicks,
        ],
        filters: {
          dateRange: null,
          dateFrom: range.dateFrom,
          dateTo: range.dateTo,
          exchangeNetworkOnly: false,
        },
      };
      const periodDays =
        Math.round((Date.parse(range.dateTo) - Date.parse(range.dateFrom)) / 86400000) + 1;
      const previousRequest = buildTimeShiftReportRequest(request, periodDays);
      const [current, previous] = await Promise.all([
        api.run(request, ReportExplorerScope.Agency, agencyId, signal),
        previousRequest
          ? api.run(previousRequest, ReportExplorerScope.Agency, agencyId, signal)
          : null,
      ]);
      return {
        previousPeriodEnd: null,
        total: reportDashboardMetricValues(current.total),
        totalPrevious: previous?.total ? reportDashboardMetricValues(previous.total) : null,
        hours: [],
        totalsByDay: current.report.flatMap((row) =>
          typeof row.day === 'string'
            ? [{ day: row.day, ...reportDashboardMetricValues(row) }]
            : [],
        ),
      };
    },
  };
}

export function useAgencyDashboardQuery(agencyId: string, range: DashboardDateRange) {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  const client = createAgencyDashboardClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session?.accessToken ?? '',
  });
  const queryKey = ['agency', 'dashboard', config.networkId, agencyId, dashboardRangeKey(range)];
  const query = useQuery({
    queryKey,
    // Reusable across StrictMode remounts; aborted only when the date range moves on.
    queryFn: supersedableDashboardQueryFn(queryKey, (signal) =>
      client.report(agencyId, range, signal),
    ),
    enabled: Boolean(session?.accessToken && agencyId),
  });
  useAbortSupersededDashboardQueries([queryKey]);
  return query;
}
